import { IApiSuccessWrapper } from '$/types'
import { apiSlice } from 'app/services/api'
import { IAttributeOption } from 'features/RiskManagement/Assessments/Scenarios/whatIfScenarioSlice'
import { IQRAScenario } from 'features/RiskManagement/Assessments/Scenarios/types'

export const qraAssessmentScenariosApi = apiSlice
  .enhanceEndpoints({
    addTagTypes: ['QRAScenario', 'QRAScenarioAttribute']
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getQRAScenarios: build.query<IQRAScenario[], void>({
        query: () => '/assessmentScenarios',
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as IQRAScenario[]
        },
        providesTags: (res) => {
          const tags = [{ type: 'QRAScenario' as const, id: 'LIST' }]

          if (res != null) {
            tags.push(
              ...res.map((scenario) => ({
                type: 'QRAScenario' as const,
                id: scenario.id.toString()
              }))
            )
          }

          return tags
        }
      }),
      getQRAScenariosByThreatId: build.query<IQRAScenario[], string>({
        query: (threatId) =>
          `/assessmentScenarios/scenario?threatId=${threatId}`,
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as IQRAScenario[]
        },
        providesTags: (res) => {
          const tags = []

          if (res != null) {
            tags.push(
              ...res.map((scenario) => ({
                type: 'QRAScenario' as const,
                id: scenario.id.toString()
              }))
            )
          }

          return tags
        }
      }),
      getQRAScenario: build.query<IQRAScenario, string>({
        query: (id) => `/assessmentScenarios/${id}`,
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as IQRAScenario
        },
        providesTags: (_res, _err, id) => [{ type: 'QRAScenario', id }]
      }),
      getQRAScenarioAttributesByThreatId: build.query<
        IAttributeOption[],
        string
      >({
        query: (threatId) =>
          `/assessmentScenarios/scenarioDetail?threatId=${threatId}`,
        transformResponse: (
          res: IApiSuccessWrapper<{
            attributes: IAttributeOption[]
          }>
        ) => {
          return res.result.attributes
        },
        providesTags: (res) => {
          const tags = []

          if (res != null) {
            tags.push(
              ...res.map((attribute) => ({
                type: 'QRAScenarioAttribute' as const,
                id: attribute.id.toString()
              }))
            )
          }

          return tags
        }
      }),
      getQRAScenarioAttributesByCode: build.query<IAttributeOption[], string>({
        query: (selectedAttribute) =>
          `/assessmentScenarios/attributeEnum?attributeCode=${selectedAttribute}`,
        transformResponse: (res: IApiSuccessWrapper<IAttributeOption[]>) => {
          return res.result
        },
        providesTags: (res) => {
          const tags = []

          if (res != null) {
            tags.push(
              ...res.map((option) => ({
                type: 'QRAScenarioAttribute' as const,
                id: option.id
              }))
            )
          }

          return tags
        }
      }),
      addQRAScenario: build.mutation<IQRAScenario, Partial<IQRAScenario>>({
        query: (scenario) => ({
          url: '/assessmentScenarios',
          method: 'POST',
          body: scenario
        }),
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as IQRAScenario
        },
        invalidatesTags: (_res, _err, scenario) => [
          { type: 'QRAScenario', id: 'LIST' },
          { type: 'QRAScenario', id: scenario.id }
        ]
      }),
      updateQRAScenario: build.mutation<IQRAScenario, Partial<IQRAScenario>>({
        query: (scenario) => ({
          url: `/assessmentScenarios/${scenario.id}`,
          method: 'PUT',
          body: scenario
        }),
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as IQRAScenario
        },
        invalidatesTags: (_res, _err, scenario) => [
          { type: 'QRAScenario', id: 'LIST' },
          { type: 'QRAScenario', id: scenario.id }
        ]
      })
    })
  })

export const {
  useGetQRAScenariosQuery,
  useGetQRAScenariosByThreatIdQuery,
  useGetQRAScenarioQuery,
  useGetQRAScenarioAttributesByThreatIdQuery,
  useGetQRAScenarioAttributesByCodeQuery,
  useAddQRAScenarioMutation,
  useUpdateQRAScenarioMutation
} = qraAssessmentScenariosApi
